// Pure helpers for the estimate-style quote print (QuoteEstimatePrint).
// Groups the quote's material + labor rows by productStructureId and computes
// the Base Bid (primary flooring categories) and grand Total. Side-effect free
// so it can be unit-tested in isolation.
import {
  CARPET_ID_DB,
  SHEET_VINYL_ID_DB,
  VINYL_PLANK_ID_DB,
} from "../../../_utils/constants/ProductCatalog";

// The estimate print has exactly 4 category groups. Only the three flooring
// products map to their own group; everything else (wall base, transitions,
// installation accessories, sundries, unmapped ids) belongs under "Wall Base",
// the catch-all — so every item shows within one of the 4 groups.
export const WALL_BASE_CATEGORY = "Wall Base";
export const CATEGORY_MAP = {
  [CARPET_ID_DB]: "Carpet", // 142
  [SHEET_VINYL_ID_DB]: "Sheet Vinyl", // 143
  [VINYL_PLANK_ID_DB]: "Vinyl Plank", // 168
};

// Display order of the 4 groups.
export const CATEGORY_NAME_ORDER = ["Carpet", "Sheet Vinyl", "Vinyl Plank", WALL_BASE_CATEGORY];

// Categories that count toward the Base Bid (mirrors estimate PRIMARY_MATERIAL_CATEGORIES).
export const PRIMARY_MATERIAL_CATEGORIES = ["Carpet", "Sheet Vinyl", "Vinyl Plank"];

// Category NAME (as it appears in laborCategory / product-structure labels) ->
// one of the flooring groups. Any other non-empty name is a non-flooring
// category, so it belongs to the Wall Base catch-all.
const NAME_TO_CATEGORY = {
  carpet: "Carpet",
  "sheet vinyl": "Sheet Vinyl",
  "vinyl plank": "Vinyl Plank",
};

const num = (v) => parseFloat(v) || 0;

const categoryNameFor = (productStructureId) =>
  CATEGORY_MAP[parseInt(productStructureId, 10)] || WALL_BASE_CATEGORY;

// Resolve a group from a category-name string (e.g. laborCategory "Vinyl Plank ").
// Returns null for an empty/absent name so callers can fall back to the id.
const categoryFromName = (name) => {
  const key = String(name || "").trim().toLowerCase();
  if (!key) return null;
  return NAME_TO_CATEGORY[key] || WALL_BASE_CATEGORY;
};

// Material/product rows carry a reliable productStructureId (kit materials keep
// the flooring structure), so the id drives the group.
const materialCategoryName = (row) => categoryNameFor(row.productStructureId);

// Labor rows carry the real group as a NAME in laborCategory — a kit labor's
// productStructureId is the kit's structure (e.g. 173), not the flooring, so
// laborCategory ("Vinyl Plank") wins; fall back to the id, then Wall Base.
const laborCategoryName = (row) =>
  categoryFromName(row.laborCategory) || categoryNameFor(row.productStructureId);

const categorySortKey = (name) => {
  const idx = CATEGORY_NAME_ORDER.indexOf(name);
  return idx !== -1 ? idx : 999;
};

// Material label: style (+ color) first, private name only as fallback.
export function materialLineName(row) {
  const style = row.product || row.style || "";
  const color = row.color || "";
  const styled = [style, color].filter(Boolean).join(" ").trim();
  if (styled) return styled;
  const priv = row.privateName || row.pvtName || "";
  if (priv && priv !== "null") return priv;
  return "";
}

export function laborLineName(row) {
  return row.labor || row.laborName || "";
}

function toItem(name, row) {
  return {
    name,
    qty: num(row.quantity),
    price: num(row.unitPrice != null ? row.unitPrice : row.price),
    lineTotal: num(row.linePrice),
  };
}

// Ordered, non-empty category groups merging material + labor into the 4
// estimate categories. Items are bucketed by category name; anything that is
// not Carpet/Sheet Vinyl/Vinyl Plank (incl. kit/sundry/accessory items and
// unmapped ids) collapses into the "Wall Base" catch-all group.
export function buildQuoteEstimateGroups({ materialListData = [], laborData = [] } = {}) {
  const buckets = new Map();

  const bucketByName = (name) => {
    if (!buckets.has(name)) {
      buckets.set(name, {
        name,
        primary: PRIMARY_MATERIAL_CATEGORIES.includes(name),
        sortKey: categorySortKey(name),
        items: [],
      });
    }
    return buckets.get(name);
  };

  (materialListData || []).forEach((row) =>
    bucketByName(materialCategoryName(row)).items.push(toItem(materialLineName(row), row))
  );
  (laborData || []).forEach((row) =>
    bucketByName(laborCategoryName(row)).items.push(toItem(laborLineName(row), row))
  );

  return Array.from(buckets.values())
    .filter((g) => g.items.length > 0)
    .sort((a, b) => a.sortKey - b.sortKey);
}

// Base Bid = primary-group line totals; Total = all-group line totals.
// lastPrimaryName anchors where the Base Bid row renders (null = no primary items).
export function computeQuoteEstimateTotals(groups = []) {
  const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
  let baseBid = 0;
  let total = 0;
  let lastPrimaryName = null;
  groups.forEach((g) => {
    const groupTotal = g.items.reduce((s, it) => s + it.lineTotal, 0);
    total += groupTotal;
    if (g.primary) {
      baseBid += groupTotal;
      lastPrimaryName = g.name;
    }
  });
  return { baseBid: round2(baseBid), total: round2(total), lastPrimaryName };
}
